import { getOwnProperty } from "@utils/getOwnProperty";
/**
 * Navigation Utilities
 *
 * This file provides icon mappings for navigation items.
 * The actual menu structure is defined in src/common/config.ts (single source of truth).
 * Both TopNavBar and SubNavTabs derive their menus from config.ts.
 */

import type { NavigationSubItem } from "@types";
import {
  Archive,
  Ban,
  BarChart3,
  Bell,
  Calendar,
  CalendarDays,
  ChartNoAxesCombined,
  CircleCheck,
  CircleOff,
  Clock,
  Contact,
  CreditCard,
  Download,
  GitBranch,
  Headset,
  History,
  LayoutDashboard,
  LayoutGrid,
  LayoutList,
  ListOrdered,
  type LucideIcon,
  MapPin,
  MessageCircle,
  MessageCircleQuestion,
  MessagesSquare,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Search,
  Settings,
  ShieldCheck,
  Store,
  UserCheck,
  Users,
  UsersRound,
  Wrench,
} from "lucide-react";

// Icon mapping for all submenu items (Lucide icons)
export const subMenuIcons = {
  // Settings submenu (merged Company + Account workspace)
  "/settings": Store,
  "/settings/hours": Clock,
  "/settings/holidays": CalendarDays,
  "/settings/service-areas": MapPin,
  "/settings/custom-analysis": BarChart3,
  "/settings/transfer-destinations": Contact,
  "/settings/custom-services": Wrench,
  "/settings/pricing-lookup": Search,
  "/settings/services-not-offered": Ban,
  "/settings/faqs": MessageCircleQuestion,
  "/settings/reports": ChartNoAxesCombined,
  "/settings/data-privacy": Download,
  "/settings/agent-memory": History,
  "/settings/archived-chats": Archive,
  "/settings/inbound-consent": PhoneIncoming,
  "/settings/outbound-consent": PhoneOutgoing,
  // Company expansion left the customer Settings submenu for the Super hub
  // (SD-10928); its icon lives in superNav.ts now.
  "/settings/team": Users,
  "/settings/billing": CreditCard,
  "/settings/notifications": Bell,
  "/chat-widget": MessageCircle,
  "/booking-wizard": Calendar,
  // Phone workspace submenu
  "/phone/dashboard": LayoutDashboard,
  "/phone/calls": History,
  "/phone/seats": Headset,
  "/phone/queues": ListOrdered,
  "/phone/numbers": Phone,
  "/phone/port-orders": ListOrdered,
  "/phone/compliance": ShieldCheck,
  "/settings/phone-numbers": Phone,
  "/settings/texting-registration": MessagesSquare,
  "/settings/blocked-callers": Ban,
  "/phone/my-settings": Settings,
  // Coach submenu
  "/coach": LayoutGrid,
  "/coach/overview": LayoutGrid,
  "/coach/conversations": MessagesSquare,
  "/coach/agent-performance": UserCheck,
  "/coach/team-performance": UsersRound,
  "/coach/settings": Settings,
  // Campaigns submenu
  "/campaigns": LayoutList,
  "/campaigns/active": CircleCheck,
  "/campaigns/inactive": CircleOff,
  // Agents submenu
  "/agents": Users,
  "/agents/playbooks": GitBranch,
} satisfies Record<string, LucideIcon>;

// Lordicon key mapping for submenu items
const subMenuLordiconKeys = {
  // Settings submenu (merged Company + Account workspace)
  "/settings": "store",
  "/settings/hours": "clock",
  "/settings/holidays": "calendar",
  "/settings/service-areas": "map-pin",
  "/settings/custom-analysis": "chart",
  "/settings/transfer-destinations": "contact",
  "/settings/reports": "chart",
  "/settings/custom-services": "wrench",
  "/settings/pricing-lookup": "search",
  "/settings/data-privacy": "download",
  "/settings/inbound-consent": "phone",
  "/settings/outbound-consent": "phone",
  "/settings/team": "users",
  "/settings/billing": "credit-card",
  "/settings/notifications": "bell",
  "/chat-widget": "chat-widget",
  "/booking-wizard": "calendar",
  // Phone workspace submenu
  "/phone/dashboard": "dashboard",
  "/phone/calls": "clock",
  "/phone/seats": "users",
  "/phone/queues": "list",
  "/phone/numbers": "phone",
  "/phone/port-orders": "list",
  "/phone/compliance": "shield",
  "/settings/phone-numbers": "phone",
  "/settings/texting-registration": "messages",
  "/settings/blocked-callers": "error",
  "/phone/my-settings": "settings",
  // Coach submenu
  "/coach": "dashboard",
  "/coach/overview": "dashboard",
  "/coach/conversations": "messages",
  "/coach/agent-performance": "user-check",
  "/coach/team-performance": "users",
  "/coach/settings": "settings",
} satisfies Record<string, string>;

// Helper to convert config submenu items to NavItem children format.
export function configToNavChildren(items: NavigationSubItem[]): {
  href: string;
  label: string;
  icon: LucideIcon;
  lordiconKey: string;
}[] {
  return items.map((item) => ({
    href: item.href,
    label: item.title,
    icon: getOwnProperty(subMenuIcons, item.href) || Wrench,
    lordiconKey: getOwnProperty(subMenuLordiconKeys, item.href) || "wrench",
  }));
}

/**
 * Phone section only relevant to A2P (US) accounts — the same gate the legacy
 * `/account/texting` tab used before the FE-1 consolidation. The 10DLC content
 * now lives on the always-visible Settings → Texting Registration page
 * (SD-15879), which self-explains for non-US accounts, so no live nav item
 * matches this any more; the gate is retained for the pure
 * `isNavItemVisibleForCompany` contract. Deliberately NOT repointed at
 * `/settings/texting-registration` — that would hide a destination non-US
 * accounts can see today.
 *
 * This dead path is checked before the Phone System prefix gate below, so it
 * keeps answering on `requiresA2P`. That stays inert precisely because nothing
 * links to it; `/phone/texting` itself just redirects to Settings.
 */
const A2P_ONLY_PHONE_PATH = "/phone/texting";

/**
 * Pricing Lookup only makes sense once a pricing book is active, so it's hidden
 * until then.
 */
const PRICING_LOOKUP_PATH = "/settings/pricing-lookup";

/**
 * The whole Phone workspace requires the Sameday Phone System
 * (`expansion.phone.enabled`).
 *
 * This used to be a per-path allowlist, because `/phone` also hosted number
 * management and A2P 10DLC registration — surfaces every account needs,
 * including the ones with no Phone System. SD-15879 moved both to Settings, so
 * `/phone/*` is now entirely Phone System and one prefix test replaces the
 * list.
 */
const PHONE_SYSTEM_PATH_PREFIX = "/phone";

/** Company-data conditions that gate individual nav destinations. */
type CompanyNavGates = {
  /** An imported pricing book is active — gates Settings → Pricing Lookup. */
  hasActivePricingBook: boolean;
  /** US company, so A2P registration applies — gates Phone → Texting. */
  requiresA2P: boolean;
  /** Company is entitled to the Phone System — gates the softphone sections. */
  phoneSystemEnabled: boolean;
};

/**
 * Is this nav destination meaningful for the current company's data? Role and
 * permission gating live elsewhere (`filterReachableNav`) — this covers the
 * few sections that only exist when the company has certain data. The ONE
 * mapping of href → condition, shared by `getVisiblePhoneSections`,
 * `getVisibleSettingsSections`, and the global search palette so the surfaces
 * can't drift apart (a destination hidden in nav must not surface in search).
 */
export function isNavItemVisibleForCompany(
  href: string,
  gates: CompanyNavGates,
): boolean {
  if (href === PRICING_LOOKUP_PATH) return gates.hasActivePricingBook;
  if (href === A2P_ONLY_PHONE_PATH) return gates.requiresA2P;
  if (
    href === PHONE_SYSTEM_PATH_PREFIX ||
    href.startsWith(`${PHONE_SYSTEM_PATH_PREFIX}/`)
  ) {
    return gates.phoneSystemEnabled;
  }
  return true;
}

/**
 * Phone sections the current user should see. Applies the shared gating —
 * per-section role/access (`userHasAccess`) plus the shared company-data gate
 * (`isNavItemVisibleForCompany`) — so the Phone sub-nav (`SubNavTabs`) and any
 * other consumer of the phone menu stay in sync from one place.
 */
export function getVisiblePhoneSections(
  items: NavigationSubItem[],
  userHasAccess: (item: NavigationSubItem) => boolean,
  requiresA2P: boolean,
): NavigationSubItem[] {
  return items
    .filter((item) => userHasAccess(item))
    .filter((item) =>
      // Phone items are never pricing-gated, so that gate is inert here. The
      // Phone System gate is inert too: this builds the sidebar *inside* the
      // workspace, which `PhoneGate` already replaced with the teaser for an
      // unentitled company.
      isNavItemVisibleForCompany(item.href, {
        requiresA2P,
        phoneSystemEnabled: true,
        hasActivePricingBook: true,
      }),
    );
}

/**
 * Settings sections the current user should see. Applies the shared gating —
 * per-section role/access (`userHasAccess`) plus the active-pricing-book gate on
 * Pricing Lookup — so the Settings left sidebar (`SettingsWorkspace.tsx`) stays
 * in sync with the config source of truth from one place.
 */
export function getVisibleSettingsSections(
  items: NavigationSubItem[],
  userHasAccess: (item: NavigationSubItem) => boolean,
  hasActivePricingBook: boolean,
): NavigationSubItem[] {
  return items
    .filter((item) => userHasAccess(item))
    .filter((item) =>
      // Settings items are never A2P-gated, so that gate is inert here.
      isNavItemVisibleForCompany(item.href, {
        hasActivePricingBook,
        // Settings holds no A2P- or Phone-System-gated destination, so both
        // gates are inert here.
        requiresA2P: true,
        phoneSystemEnabled: true,
      }),
    );
}
